import React from 'react';
import { Button, Chip, Divider } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getSiteAuditIcons } from 'src/common/config/FunctionHelper';
import TitleTooltip from 'src/components/UI/TitleTooltip';
import { ContentStrategyAIInsightsV2 } from '../../types/content-strategy-v2.type';

interface AIInsightsPanelProps {
  insights?: ContentStrategyAIInsightsV2;
  onSelectOpportunity: (opportunityId: string) => void;
}

const INSIGHT_CARDS = [
  {
    key: 'highest_roi',
    label: 'Highest ROI',
    chipClass: '!bg-[#E6F9F7] !text-[#0D6F66] !font-medium',
  },
  {
    key: 'quick_wins',
    label: 'Quick Wins',
    chipClass: '!bg-[#FEF0C7] !text-[#B54708] !font-medium',
  },
  {
    key: 'trending',
    label: 'Trending Topics',
    chipClass: '!bg-[#F4F3FF] !text-[#6941C6] !font-medium',
  },
  {
    key: 'commercial',
    label: 'Missing Commercial Pages',
    chipClass: '!bg-[#FEE4E2] !text-[#D92D20] !font-medium',
  },
] as const;

export const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  insights,
  onSelectOpportunity
}) => {
  if (!insights) return null;

  const highestRoi = insights.highest_roi_opportunity;
  const quickWins = insights.quick_wins || [];
  const trending = insights.trending_topics || [];
  const missingCommercial = insights.missing_commercial_pages || [];

  const firstQuickWin = quickWins[0];
  const firstTrending = trending[0];
  const firstCommercial = missingCommercial[0];

  const cards = [
    highestRoi
      ? {
          ...INSIGHT_CARDS[0],
          title: highestRoi.suggested_title || highestRoi.topic,
          subtitle: highestRoi.topic,
          extra: '',
          id: highestRoi.id,
          cta: 'View opportunity',
        }
      : null,
    firstQuickWin
      ? {
          ...INSIGHT_CARDS[1],
          title: firstQuickWin.suggested_title || firstQuickWin.topic,
          subtitle: firstQuickWin.topic,
          extra:
            quickWins.length > 1
              ? `+${quickWins.length - 1} more quick wins identified`
              : '',
          id: firstQuickWin.id,
          cta: 'View quick win',
        }
      : null,
    firstTrending
      ? {
          ...INSIGHT_CARDS[2],
          title: firstTrending.suggested_title || firstTrending.topic,
          subtitle: firstTrending.topic,
          extra:
            trending.length > 1
              ? `+${trending.length - 1} trending topics`
              : '',
          id: firstTrending.id,
          cta: 'View trending topic',
        }
      : null,
    firstCommercial
      ? {
          ...INSIGHT_CARDS[3],
          title: firstCommercial.suggested_title || firstCommercial.topic,
          subtitle: firstCommercial.topic,
          extra:
            missingCommercial.length > 1
              ? `+${missingCommercial.length - 1} high-intent pages`
              : '',
          id: firstCommercial.id,
          cta: 'View commercial page',
        }
      : null,
  ].filter(Boolean);

  if (cards.length === 0) return null;

  return (
    <div>
      {/* Header — matches AISummary / ContentOpportunities pattern */}
      <div className="px-6 py-2">
        <div className="flex items-center gap-2">
          <TitleTooltip>
            <TitleTooltip.Text
              title="AI Insights"
              className="!text-[var(--color-text-secondary)] !font-medium !text-base"
            >
              <TitleTooltip.Tooltip
                tooltip={
                  <p className="text-primary text-sm font-medium">
                    AI-curated high-priority starting points based on ROI
                    potential, quick wins, trending topics, and commercial
                    opportunities.
                  </p>
                }
              />
            </TitleTooltip.Text>
          </TitleTooltip>
        </div>
      </div>

      {/* Cards */}
      <div className="px-6 py-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => {
          if (!card) return null;

          return (
            <div
              key={card.key}
              className="border border-[#EAECF0] rounded-lg p-4 flex flex-col justify-between bg-white hover:shadow-sm transition-shadow"
            >
              <div>
                {/* Category badge */}
                <div className="mb-3">
                  <Chip
                    label={
                      <span className="text-xs font-medium">{card.label}</span>
                    }
                    size="small"
                    className={card.chipClass}
                  />
                </div>

                {/* Title */}
                <p className="font-semibold text-sm text-[#1D2939] leading-snug line-clamp-2 mb-1">
                  {card.title}
                </p>

                {/* Subtitle / keyword */}
                <p className="text-xs text-[#667085] line-clamp-1 m-0">
                  {card.extra || card.subtitle}
                </p>
              </div>

              {/* CTA — uses MUI Button matching project pattern */}
              <Button
                variant="text"
                size="small"
                onClick={() => onSelectOpportunity(card.id)}
                sx={{
                  mt: 2,
                  p: 0,
                  minWidth: 0,
                  justifyContent: 'flex-start',
                  color: '#120242',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#0D6F66',
                  },
                }}
              >
                {card.cta}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AIInsightsPanel;
